import { getClientAttributes } from './services';
import { ClientSelectorModal } from './ClientSelectorModal';
import { useState, useEffect, ReactNode, useCallback, Fragment } from 'react';
import { ClientAttributes } from '@ssar-app/domain';

export interface ClientAttributesSelectorModel {
  subscriberCode: string;
  clientAttributes: ClientAttributes;
}
export interface ClientAttributesSelectorProps {
  subscriberCodes: string[];
  canSearchAndSelect: boolean;
  onSuccess: (args: ClientAttributesSelectorModel) => void;
  shouldReturnNode?: boolean;
  onClientAttributesInfoAvailable?: (
    args: ClientAttributesSelectorModel,
  ) => ReactNode;
}

export const ClientAttributesSelector = (
  props: ClientAttributesSelectorProps,
) => {
  const {
    subscriberCodes,
    canSearchAndSelect,
    onSuccess: onClientAttributesAvailable,
    shouldReturnNode,
    onClientAttributesInfoAvailable,
  } = props;
  const [clientAttributesError, setClientAttributesError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState<ClientAttributesSelectorModel>();

  const getClientAttributesFn = useCallback((subscriberCode: string) => {
    if (subscriberCode) {
      setLoading(true);
      getClientAttributes(subscriberCode)
        .then((response) => {
          setLoading(false);
          if (response.data?.details?.state?.toUpperCase() !== 'SUCCESS') {
            setClientAttributesError(
              response.data?.details?.message ||
                'Unknown error while fetching Client Attributes',
            );
          } else if (response.resultCode === 0) {
            if (response.data) {
              setModel({
                subscriberCode,
                clientAttributes: response.data,
              });
            } else {
              setClientAttributesError(
                `Invalid Subscriber Code: ${subscriberCode}`,
              );
            }
          } else {
            setClientAttributesError(
              `Error while fetching Client Attributes: ${response.messages.join(
                '\\n',
              )}`,
            );
          }
        })
        .catch((err) => {
          setLoading(false);
          const message = (err.response?.data?.messages || [err.message]).join(
            '\\n',
          );
          setClientAttributesError(
            `Error while fetching Client Attributes: ${message}`,
          );
        });
    }
  }, []);

  const foundClientAttributes =
    model && model.clientAttributes && model.subscriberCode;

  useEffect(() => {
    if (subscriberCodes.length > 0) {
      if (subscriberCodes.length === 1) {
        getClientAttributesFn(subscriberCodes[0]);
      }
    } else if (!canSearchAndSelect) {
      setClientAttributesError('Subscriber Code was not found.');
    }
  }, [canSearchAndSelect, getClientAttributesFn, subscriberCodes]);

  useEffect(() => {
    if (!shouldReturnNode && foundClientAttributes && model) {
      onClientAttributesAvailable({
        subscriberCode: model.subscriberCode,
        clientAttributes: model.clientAttributes,
      });
    }
  }, [
    foundClientAttributes,
    model,
    onClientAttributesAvailable,
    shouldReturnNode,
  ]);
  const shouldShowComponent = !(foundClientAttributes && !!model);
  return (
    <Fragment>
      {shouldShowComponent && (
        <ClientSelectorModal
          isLoading={loading}
          subscriberCodes={subscriberCodes}
          errorMsg={clientAttributesError}
          onClick={getClientAttributesFn}
          showModal={
            !shouldReturnNode &&
            !foundClientAttributes &&
            (subscriberCodes.length > 1 || canSearchAndSelect)
          }
        />
      )}
      {shouldReturnNode &&
        foundClientAttributes &&
        model &&
        onClientAttributesInfoAvailable &&
        onClientAttributesInfoAvailable({
          subscriberCode: model.subscriberCode,
          clientAttributes: model.clientAttributes,
        })}
    </Fragment>
  );
};
